import { useEffect } from "react";

const HREFLANG_ATTR = "data-kreuse-hreflang";

function setHreflang(hreflang, href) {
  let link = document.querySelector(`link[${HREFLANG_ATTR}="${hreflang}"]`);
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute(HREFLANG_ATTR, hreflang);
    link.setAttribute("hreflang", hreflang);
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function clearHreflang() {
  document
    .querySelectorAll(`link[${HREFLANG_ATTR}]`)
    .forEach((el) => el.remove());
}

/**
 * useSEO
 * @param {object} opts
 *  - title, description, canonical, jsonLd
 *  - hreflangs: { fr?: string, en?: string, xDefault?: string } — inject <link rel="alternate" hreflang="...">.
 *    When canonical is given but hreflangs is omitted, FR-only hreflang is auto-set on canonical.
 */
const clamp = (text, max) => {
  if (!text || text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  return `${cut.slice(0, cut.lastIndexOf(" ")).replace(/[\s,;:—-]+$/, "")}…`;
};

export function useSEO({ title: rawTitle, description: rawDescription, canonical, jsonLd, hreflangs }) {
  const title = clamp(rawTitle, 60);
  const description = clamp(rawDescription, 155);
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    if (canonical) {
      setMeta("og:url", canonical, "property");
      setMeta("twitter:title", title);
      setMeta("twitter:description", description);
    }

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    // hreflang alternates
    clearHreflang();
    if (hreflangs) {
      if (hreflangs.fr) setHreflang("fr", hreflangs.fr);
      if (hreflangs.en) setHreflang("en", hreflangs.en);
      setHreflang(
        "x-default",
        hreflangs.xDefault || hreflangs.fr || canonical || "",
      );
    } else if (canonical) {
      // Default: this page is French-only (legal, blog…)
      setHreflang("fr", canonical);
      setHreflang("x-default", canonical);
    }

    // Per-page JSON-LD
    const SCRIPT_ID = "page-jsonld";
    const old = document.getElementById(SCRIPT_ID);
    if (old) old.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = SCRIPT_ID;
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
    return () => {
      const cur = document.getElementById(SCRIPT_ID);
      if (cur) cur.remove();
    };
  }, [title, description, canonical, jsonLd, hreflangs]);
}
